import moment from 'moment';
import React, { useState } from 'react';
import Button from '../../../components/form-control/Button';
import TextArea from '../../../components/form-control/TextArea';
import { ICustomerUpdate } from '../../../models/customer/ICustomer';
import useUserAccount from '../../../hooks/userAccountHook';
import { Calendar, Clock, MoreVertical, StickyNote, User } from 'lucide-react';
import { Customer } from '../../../models/customer/Customer';
import { addCustomerUpdate } from '../../../api/requests';
import { Drawer } from '../../../components/elements/Drawer';
import { showToast } from '../../../utils/miscFunctions';

interface ICustomerUpdates {
  onProceed: () => void;
  onClose: () => void;
  show: boolean;
  updates: ICustomerUpdate[];
  custumorId: string;
  onCustomerUpdated: (customer: Customer) => void;
}

const CustomerUpdates = ({ onProceed, onClose, show = false, updates, custumorId, onCustomerUpdated }: ICustomerUpdates) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [reply, setReply] = useState<string>('');
  const { user } = useUserAccount();
  const [activeNoteMenu, setActiveNoteMenu] = useState<string | null>(null);

  const filteredNotes = updates.sort((a, b) => { return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(); }) || [];


  const handleSubmit = () => {
    if (!reply) {
      showToast.error('You need to provide a message to proceed.');
      return;
    }

    setSubmitting(true);
    addCustomerUpdate(custumorId, { description: reply })
      .then((response: any) => {
        if (response) {
          onCustomerUpdated(new Customer(response?.data));
          setReply('');
        }
      })
      .finally(() => setSubmitting(false));
  };

  const footer= ()=>{
    return(
      <div className="px-6 py-6 bg-gray-50 border-t border-gray-200 w-full">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span className="flex items-center">
              <Clock className="h-4 w-4 mr-1.5" />
              Last updated: {new Date().toLocaleDateString()}
            </span>
            <span className="font-medium text-gray-700">
              {updates.length} notes
            </span>
          </div>
        </div>
    )
  }


  return (
    <Drawer isOpen={show} onClose={onClose} position="right" size={700} header={
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-5 flex items-center justify-between shadow-lg w-full">
          <div className="flex items-center">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
              <StickyNote className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Customer Notes</h2>
              <p className="text-gray-300 text-sm">CUST-{custumorId}</p>
            </div>
          </div>
        </div>
      }
      footer={footer()}
    >
      {/* Search and Stats */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 -mx-4 -mt-4 dark:bg-darkBg">
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center">
            <StickyNote className="h-4 w-4 text-gray-700 mr-1.5" />
            <span className="font-medium text-gray-700 dark:text-darkBg-mainText">{updates?.length || 0}</span>
            <span className="text-gray-500 ml-1 dark:text-darkBg-mainText">notes</span>
          </div>
        </div>
      </div>

      {/* Note Editor Section */}
      {!user.isInsurerAgent() && (
        <div className="px-6 py-4 bg-white border-b border-gray-200 -mx-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700">
                Add New Note
              </label>
            </div>
            
            <TextArea
              name={'reason'}
              value={reply}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                setReply(event.target.value);
              }}
              placeholder="Write an update..."
              rows={5}
            />
          
            <Button
              label="Save Note"
              className="w-full py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center font-medium"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
            />
          </div>
        </div>
      )}

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto px-6 py-4 -mx-4">
        {updates.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <StickyNote className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium mb-1">No notes found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotes.map((update: ICustomerUpdate, index: number) => (
              <div
                key={index}
                className="bg-white border-2 rounded-lg p-4 transition-all duration-200 hover:shadow-md border-gray-200 hover:border-gray-400"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2 flex-1">
                    <span className="inline-block px-2.5 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-200">
                      CUSTOMER
                    </span>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setActiveNoteMenu(activeNoteMenu === update.id ? null : update.id)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors duration-200"
                    >
                      <MoreVertical className="h-4 w-4 text-gray-500" />
                    </button>
                    {activeNoteMenu === update.id && (
                      <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                        <div className="w-full text-left px-3 py-2 text-sm text-gray-400 flex items-center cursor-not-allowed">
                          No actions available
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-gray-800 text-sm mb-3 whitespace-pre-wrap leading-relaxed">
                  {update.description}
                </p>

                <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-200">
                  <div className="flex items-center">
                    <User className="h-3 w-3 mr-1" />
                    {update.staff}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    {moment(update.createdOn).format('DD-MM-YYYY')} At {moment(update.createdOn).format('HH:mm:ss')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default CustomerUpdates;
